<template>
  <v-dialog
    :value="value"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    @input="handleInput"
  >
    <v-card
      tile
      :color="$vuetify.theme.isDark ? 'grey darken-4' : 'grey lighten-5'"
    >
      <v-toolbar color="transparent" flat>
        <v-toolbar-title>Mit QR-Code anmelden</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-container fluid class="py-8">
        <v-row justify="center">
          <v-col cols="12" sm="8" md="6" lg="6" xl="3">
            <!-- Generator card -->
            <v-card>
              <v-card-text class="pa-4">
                <!-- Revoked / Expired hint -->
                <v-alert
                  v-if="session && session.status === 'revoked'"
                  type="error"
                  dense
                  text
                  class="mb-4"
                >
                  Anmeldeversuch wurde widerrufen.
                </v-alert>
                <v-alert
                  v-if="session && session.status === 'expired'"
                  type="error"
                  dense
                  text
                  class="mb-4"
                >
                  Anmeldeversuch ist abgelaufen.
                </v-alert>

                <!-- No session / revoked / expired → show create UI -->
                <div
                  v-if="
                    !session ||
                    session.status === 'revoked' ||
                    session.status === 'expired'
                  "
                  class="text-center py-6"
                >
                  <v-icon size="64" color="primary" class="mb-4"
                    >mdi-qrcode-scan</v-icon
                  >
                  <p class="text-h6 mb-1">Mit QR-Code anmelden</p>
                  <p class="text-body-2 text--secondary mb-5">
                    Fordere einen QR-Code für die Anmeldung an und scanne ihn
                    mit deinem anderen Gerät ein. Nach Bestätigung der Anmeldung
                    auf diesem Gerät, wirst du automatisch eingeloggt.
                  </p>
                  <v-btn
                    color="primary"
                    large
                    depressed
                    :loading="creating"
                    :disabled="creating"
                    @click="createSession"
                  >
                    <v-icon left>mdi-qrcode-plus</v-icon>
                    Session starten
                  </v-btn>
                </div>

                <!-- Active session content -->
                <div v-else>
                  <!-- QR Code (only visible while waiting to be scanned) -->
                  <div
                    v-if="session.status === 'pending'"
                    class="text-center mb-4"
                  >
                    <div class="mb-2 font-weight-medium">
                      QR-Code mit anderem Gerät einscannen
                    </div>
                    <div
                      class="qr-wrapper d-inline-block pa-3"
                      style="position: relative"
                    >
                      <canvas ref="qrCanvas" class="qr-canvas"></canvas>
                    </div>
                  </div>

                  <!-- Countdown -->
                  <div
                    v-if="
                      session.status === 'pending' ||
                      session.status === 'requested'
                    "
                    class="text-center mb-4"
                  >
                    <v-progress-linear
                      :value="(timeRemaining / 120) * 100"
                      :color="timeRemaining > 30 ? 'primary' : 'error'"
                      rounded
                      height="6"
                      class="mb-2"
                    ></v-progress-linear>
                    <span class="text-caption text--secondary">
                      Läuft ab in {{ timeRemaining }} Sekunden
                    </span>
                  </div>

                  <!-- Requesting device info -->
                  <info-card
                    v-if="session.deviceInfo"
                    title="Anfragendes Gerät"
                    icon="mdi-shield-alert-outline"
                    :showDetails.sync="showDeviceDetails"
                  >
                    <template v-slot:details>
                      <v-card-text class="py-2">
                        <v-list dense nav>
                          <v-list-item
                            v-for="(item, index) in deviceInfoItems"
                            :key="index"
                            dense
                          >
                            <v-list-item-icon
                              class="mr-3 my-1 align-self-center"
                            >
                              <v-icon small :color="item.color">{{
                                item.icon
                              }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content class="py-1">
                              <v-list-item-subtitle class="text-caption">{{
                                item.label
                              }}</v-list-item-subtitle>
                              <v-list-item-title class="text-body-2">{{
                                item.value
                              }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-card-text>
                    </template>
                  </info-card>

                  <v-card v-if="session.approvalOtp" outlined class="mb-4">
                    <v-card-text class="py-4">
                      <div class="text-caption text--secondary mb-2">
                        Bestätigungscode
                      </div>
                      <div class="text-h4 font-weight-bold">
                        {{ formatOtp(session.approvalOtp) }}
                      </div>
                      <v-alert
                        v-if="session.approvalOtpVerified"
                        type="success"
                        dense
                        text
                        class="mt-3 mb-0"
                      >
                        Der Code wurde auf dem anderen Gerät korrekt eingegeben.
                      </v-alert>
                    </v-card-text>
                  </v-card>

                  <!-- Approve / Deny -->
                  <v-row v-if="session.status === 'requested'">
                    <v-col cols="6">
                      <v-btn
                        color="success"
                        block
                        depressed
                        :loading="approving"
                        :disabled="
                          approving || revoking || !session.approvalOtpVerified
                        "
                        @click="approveSession"
                      >
                        <v-icon left>mdi-check</v-icon>
                        Genehmigen
                      </v-btn>
                    </v-col>
                    <v-col cols="6">
                      <v-btn
                        color="error"
                        outlined
                        block
                        :loading="revoking"
                        :disabled="approving || revoking"
                        @click="revokeSession"
                      >
                        <v-icon left>mdi-close</v-icon>
                        Ablehnen
                      </v-btn>
                    </v-col>
                  </v-row>

                  <!-- Approved state -->
                  <v-alert
                    v-if="
                      session.status === 'approved' ||
                      session.status === 'consumed'
                    "
                    type="success"
                    dense
                    text
                    class="mb-3"
                  >
                    <div class="text-body-2 mb-2">
                      Anmeldung genehmigt. Das andere Gerät wird eingeloggt.
                    </div>
                    <v-btn color="success" depressed small @click="close">
                      <v-icon left small>mdi-close</v-icon>
                      Fenster schließen
                    </v-btn>
                  </v-alert>

                  <!-- Reset / Cancel -->
                  <div class="d-flex justify-space-between">
                    <v-btn
                      v-if="session.status === 'pending'"
                      text
                      small
                      color="error"
                      :loading="revoking"
                      :disabled="revoking"
                      block
                      outlined
                      @click="revokeSession"
                    >
                      <v-icon left small>mdi-cancel</v-icon>
                      Abbrechen
                    </v-btn>
                  </div>
                </div>

                <!-- Error Display -->
                <v-alert
                  v-if="error"
                  type="error"
                  dense
                  text
                  class="mt-4"
                  dismissible
                  @input="error = null"
                >
                  {{ error }}
                </v-alert>
              </v-card-text>
            </v-card>

            <!-- Security note -->
            <div class="text-center mt-5">
              <v-icon small color="grey">mdi-lock-outline</v-icon>
              <span class="text-caption text--disabled ml-1">
                Anmeldeversuch wird beim Schließen dieses Dialogs automatisch
                beendet.
              </span>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import { functions, db } from "@/firebase.js";
import InfoCard from "@/components/_systemwide/InfoCard.vue";
import QRCode from "qrcode";

export default {
  name: "QRLoginGeneratorDialog",
  components: {
    InfoCard,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      session: null,
      showDeviceDetails: true,
      creating: false,
      approving: false,
      revoking: false,
      error: null,
      unsubscribeListener: null,
      timeRemaining: 0,
      countdownInterval: null,
    };
  },
  computed: {
    statusColor() {
      switch (this.session?.status) {
        case "pending":
          return "orange";
        case "requested":
          return "blue";
        case "approved":
          return "green";
        case "consumed":
          return "success";
        case "revoked":
          return "error";
        case "expired":
          return "grey";
        default:
          return "grey";
      }
    },
    statusIcon() {
      switch (this.session?.status) {
        case "pending":
          return "mdi-clock-outline";
        case "requested":
          return "mdi-cellphone-arrow-down";
        case "approved":
          return "mdi-check-circle";
        case "consumed":
          return "mdi-check-circle";
        case "revoked":
          return "mdi-cancel";
        case "expired":
          return "mdi-timer-off";
        default:
          return "mdi-help";
      }
    },
    statusText() {
      switch (this.session?.status) {
        case "pending":
          return "Warte auf Scan …";
        case "requested":
          return "Anfrage erhalten";
        case "approved":
          return "Genehmigt";
        case "consumed":
          return "Abgeschlossen";
        case "revoked":
          return "Widerrufen";
        case "expired":
          return "Abgelaufen";
        default:
          return "Unbekannt";
      }
    },
    deviceInfoItems() {
      if (!this.session?.deviceInfo) return [];
      const info = this.session.deviceInfo;
      const items = [];

      if (info.name || info.type) {
        items.push({
          icon: this.getDeviceTypeIcon(info.type),
          label: "Gerät",
          value: info.name || info.type || "Unbekannt",
        });
      }

      if (info.browser && info.browser.name) {
        items.push({
          icon: "mdi-web",
          label: "Browser",
          value:
            info.browser.name +
            (info.browser.version ? " " + info.browser.version : ""),
        });
      }

      if (info.os) {
        items.push({
          icon: "mdi-laptop",
          label: "Betriebssystem",
          value: info.os,
        });
      }

      if (info.screenResolution) {
        items.push({
          icon: "mdi-monitor-screenshot",
          label: "Bildschirm",
          value: info.screenResolution,
        });
      }

      if (info.language) {
        items.push({
          icon: "mdi-translate",
          label: "Sprache",
          value: this.formatLanguage(info.language),
        });
      }

      if (info.timezone) {
        items.push({
          icon: "mdi-map-marker-outline",
          label: "Zeitzone",
          value: info.timezone,
        });
      }

      if (info.location && (info.location.city || info.location.country)) {
        const parts = [
          info.location.city,
          info.location.region,
          info.location.country,
        ].filter(Boolean);
        items.push({
          icon: "mdi-map-marker",
          label: "Standort (ca.)",
          value: parts.join(", "),
        });
      }

      if (info.ip) {
        items.push({
          icon: "mdi-ip-network-outline",
          label: "IP-Adresse",
          value: info.ip,
        });
      }

      return items;
    },
  },
  watch: {
    value(open) {
      if (!open) {
        this.cleanup();
      }
    },
  },
  methods: {
    handleInput(val) {
      this.$emit("input", val);
    },
    close() {
      this.$emit("input", false);
    },
    cleanup() {
      this.stopListening();
      this.stopCountdown();
      this.session = null;
      this.error = null;
      this.creating = false;
      this.approving = false;
      this.revoking = false;
    },

    async createSession() {
      this.creating = true;
      this.error = null;
      this.approvalOtpInput = "";

      try {
        const createSessionFn = functions.httpsCallable(
          "qrLogin-createSession",
        );
        const result = await createSessionFn();

        this.session = {
          sid: result.data.sid,
          transferId: result.data.transferId,
          status: "pending",
          qrPayload: result.data.qrPayload,
          deviceInfo: null,
          approvalOtp: null,
          approvalOtpVerified: false,
        };

        await this.$nextTick();
        await this.generateQRCode(result.data.qrPayload);
        this.startListening();
        this.startCountdown();
      } catch (error) {
        console.error("Fehler beim Erstellen der Session:", error);
        this.error =
          "Session konnte nicht erstellt werden. Bitte versuche es erneut.";
      } finally {
        this.creating = false;
      }
    },

    async approveSession() {
      if (!this.session) return;
      this.approving = true;
      this.error = null;

      try {
        const approveLoginFn = functions.httpsCallable("qrLogin-approveLogin");
        await approveLoginFn({ sid: this.session.sid });
        this.session.status = "approved";
      } catch (error) {
        console.error("Fehler beim Genehmigen der Session:", error);
        this.error = "Anmeldung konnte nicht genehmigt werden.";
      } finally {
        this.approving = false;
      }
    },

    async revokeSession() {
      if (!this.session) return;
      this.revoking = true;
      this.error = null;

      try {
        const revokeSessionFn = functions.httpsCallable(
          "qrLogin-revokeSession",
        );
        await revokeSessionFn({ sid: this.session.sid });
        this.session.status = "revoked";
        this.stopListening();
        this.stopCountdown();
      } catch (error) {
        console.error("Fehler beim Widerrufen der Session:", error);
        this.error = "Session konnte nicht widerrufen werden.";
      } finally {
        this.revoking = false;
      }
    },

    resetSession() {
      this.stopListening();
      this.stopCountdown();
      this.session = null;
    },

    async generateQRCode(payload) {
      try {
        const canvas = this.$refs.qrCanvas;
        await QRCode.toCanvas(canvas, payload, {
          width: 240,
          margin: 2,
          color: { dark: "#000000", light: "#FFFFFF" },
        });
      } catch (error) {
        console.error("Fehler beim Generieren des QR-Codes:", error);
        this.error = "QR-Code konnte nicht generiert werden.";
      }
    },

    startListening() {
      this.stopListening();
      if (!this.session?.transferId) return;

      this.unsubscribeListener = db
        .collection("ephemeralSessions")
        .doc(this.session.sid)
        .onSnapshot(
          (doc) => {
            if (!doc.exists || !this.session) return;

            const data = doc.data();
            const hasApprovalOtpField = Object.prototype.hasOwnProperty.call(
              data,
              "approvalOtp",
            );
            const hasDeviceInfoField = Object.prototype.hasOwnProperty.call(
              data,
              "deviceInfo",
            );
            this.session = {
              ...this.session,
              status: data.status,
              deviceInfo: hasDeviceInfoField
                ? data.deviceInfo
                : this.session.deviceInfo,
              approvalOtp: hasApprovalOtpField
                ? data.approvalOtp
                : this.session.approvalOtp,
              approvalOtpVerified:
                typeof data.approvalOtpVerified === "boolean"
                  ? data.approvalOtpVerified
                  : this.session.approvalOtpVerified,
            };

            if (data.status === "consumed") {
              this.stopListening();
              this.stopCountdown();
            }
          },
          (error) => {
            console.error("Firestore listener error:", error);
          },
        );
    },

    stopListening() {
      if (this.unsubscribeListener) {
        this.unsubscribeListener();
        this.unsubscribeListener = null;
      }
    },

    startCountdown() {
      this.timeRemaining = 120;
      this.countdownInterval = setInterval(() => {
        this.timeRemaining--;
        if (this.timeRemaining <= 0) {
          this.stopCountdown();
          this.stopListening();
          if (this.session && this.session.status !== "consumed") {
            this.session.status = "expired";
          }
        }
      }, 1000);
    },

    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },

    getDeviceTypeIcon(type) {
      switch (type) {
        case "mobile":
          return "mdi-cellphone";
        case "tablet":
          return "mdi-tablet";
        case "desktop":
          return "mdi-monitor";
        default:
          return "mdi-devices";
      }
    },

    formatLanguage(code) {
      if (!code || code === "unknown") return "Unbekannt";
      try {
        const displayNames = new Intl.DisplayNames(["de"], {
          type: "language",
        });
        return displayNames.of(code);
      } catch {
        return code;
      }
    },

    formatOtp(otp) {
      if (!otp) return "";
      return String(otp).replace(/(\d{3})(\d{3})/, "$1 $2");
    },
  },

  beforeDestroy() {
    this.cleanup();
  },
};
</script>

<style scoped>
.qr-wrapper {
  background: #fff;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
}

.qr-canvas {
  display: block;
}

.qr-revoke-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 10px;
}
</style>
